import { computed, inject, Injectable, Signal } from '@angular/core';
import { IAddressModel, IContactModel, ICustomer, IDebitsModel, IIdentityModel, ILoginData } from '@partners/api';
import { CatalogQuery, TBankBranchOption, TCatalogOption } from '@partners/catalog';
import { ErrorsService, tapSuccess } from '@partners/errors';
import { HttpService } from '@partners/http';
import {
  AddressStore,
  AuthLoginStore,
  ClassifyStore,
  ContactStore,
  CustomerStore,
  DebitsStore,
  IdentityStore,
  ShellStore
} from '@partners/state';
import { isNotEmpty, takeOnce } from '@partners/utils';
import { lastValueFrom } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class CustomerFacade {
  readonly customer$$: Signal<ICustomer>;
  #catalogQuery = inject(CatalogQuery);
  #debitsStore = inject(DebitsStore);
  #identityStore = inject(IdentityStore);
  #contactStore = inject(ContactStore);
  #addressStore = inject(AddressStore);
  #loginStore = inject(AuthLoginStore);
  #shellStore = inject(ShellStore);
  #classifyStore = inject(ClassifyStore);
  #httpService = inject(HttpService);
  #errorsService = inject(ErrorsService);
  #customerStore = inject(CustomerStore);

  readonly hasCustomerResponse$$ = computed<boolean>(() => this.#customerStore.hasCustomerResponse$$());

  /**
   * this should be the single source of truth to the latest value of customer's idNumber.
   * do not read it or expose it to the bll via any other interface
   */
  readonly idNumber$$ = computed<string>(() => {
    const hasPolicy = this.#shellStore.hasPolicy$$();

    if (hasPolicy) {
      /** the default source of the idNumber is policy data */
      return this.#shellStore.policy$$().idNum;
    }

    const classifyModel = this.#classifyStore.model$$();

    if (isNotEmpty(classifyModel.idNumber)) {
      /**
       * in case policy has not yet been acquired, we are likely running a loan process in web mode -
       * in which case we assume the classify's domain model in initialized and use it as secondary
       * optional source of the idNumber
       */
      return classifyModel.idNumber;
    }

    this.#errorsService.throwLocalError({ message: `Could not find idNumber value in the store` });

    return '';
  });

  readonly isNewCustomer$$ = computed<boolean>(() => {
    const customerData = this.customer$$();
    if (!customerData) {
      return undefined;
    }
    const { isNewCustomer } = customerData;
    return isNewCustomer;
  });

  readonly isNotNewCustomer$$ = computed<boolean>(() => {
    return this.isNewCustomer$$() !== true;
  });

  readonly isVerifiedCustomer$$ = computed<boolean>(() => {
    const customerData = this.customer$$();
    if (!customerData) {
      return undefined;
    }
    const { isVerifiedCustomer } = customerData;
    return isVerifiedCustomer;
  });

  readonly isUnVerifiedCustomer$$ = computed<boolean>(() => {
    return this.isVerifiedCustomer$$() !== true;
  });

  // #updateEmploymentModel({ employmentType }: ICustomer) {
  //   const employmentModel: IEmploymentModel = {
  //     employmentType: employmentType
  //   };

  //   /** update the contact store */
  //   this.#employmentStore.dispatchUpdateModel({ model: employmentModel });
  // }

  loadCustomer(): Promise<ILoginData> {
    this.#customerStore.dispatchCustomerLoad();
    const customer = this.#httpService.fetch<void, ILoginData>('customer').pipe(
      takeOnce(),
      tapSuccess<ILoginData>((data: ILoginData) => {
        this.#customerStore.dispatchCustomerLoadSuccess({ model: null, data });
        this.distributeLoginData(data);
      })
    );

    return lastValueFrom<ILoginData>(customer);
  }

  #updateContactModel({ displayPhoneNumber, email, firstNameEng, lastNameEng }: ICustomer) {
    const contactModel: IContactModel = {
      displayPhoneNumber,
      lastNameEng,
      firstNameEng,
      email
    };

    /** update the contact store */
    this.#contactStore.dispatchUpdateModel({ model: contactModel });
  }

  #updateAddressModel({
    residence,
    residenceName,
    street,
    streetName,
    houseNumber,
    zipCode,
    entrance,
    appartmentNum,
    email
  }: ICustomer) {
    const addressModel: IAddressModel = {
      residenceCode: residence,
      streetCode: street,
      zipCode,
      zipCode7: zipCode,
      appartmentNum,
      entrance,
      streetName,
      residenceName,
      houseNumber,
      email
    };

    /** update the address store */
    this.#addressStore.dispatchUpdateModel({ model: addressModel });
  }

  updateIdentityModel({ firstNameHeb, lastNameHeb, birthDate, gender, idIssueDate }: ICustomer) {
    const identityModel: IIdentityModel = {
      firstName: firstNameHeb,
      lastName: lastNameHeb,
      idIssueDate,
      birthDate,
      gender
    };

    this.#identityStore.dispatchUpdateModel({ model: identityModel });
  }

  #updateDebitsModel({ accountNumber, accountOwnerName, bankCode, branchCode }: IDebitsModel) {
    if (!bankCode) {
      return;
    }

    /** server only gives us bank & branch codes, we need to find the bank & branch names to display */
    const bank: TCatalogOption = this.#catalogQuery.findBank(bankCode);
    const branch: TBankBranchOption = this.#catalogQuery.findBankBranch(branchCode, bankCode);

    /** this data is meant for the debits model store */
    const debitsModel: IDebitsModel = {
      accountNumber,
      accountOwnerName,
      bankCode,
      branchCode,
      bankName: bank.key,
      branchName: branch.key
    };

    /** update the debits store */
    this.#debitsStore.dispatchUpdateModel({ model: debitsModel });
  }

  constructor() {
    this.customer$$ = computed(() => this.#loginStore.customerData$$());
  }

  ngOnDestroy(): void {}

  distributeLoginData({ customer, debits }: { customer?: ICustomer; debits?: IDebitsModel }) {
    if (customer) {
      // this.#updateEmploymentModel(customer);
      this.#updateContactModel(customer);
      this.#updateAddressModel(customer);
      this.updateIdentityModel(customer);
    }
    if (debits) {
      this.#updateDebitsModel(debits);
    }
  }
}
